<template>
    <div class="flex flex-col gap-4">
        <CFormGroup
            v-if="show('interests')"
            description="Slová oddeľuj enterom. Jeden #hashtag (kľúčové slovo) môže obsahovať max. 3 slová (po 35 znakov). Hashtagy sa oddeľujú enterom. Pomocou nich dokáže systém vyhľadávať a odporúčať."
            :error="errors.interests"
            label="S čím viem ako mentor pomôcť"
            required
        >
            <CInterestsInput
                :disabled="disabled"
                placeholder="Pridaj kľúčové slová…"
                :model-value="modelValue.interests"
                @update:model-value="update('interests', $event)"
            />

            <SuggestedKeywords
                class="mt-2"
                :disabled="disabled"
                label="Vyber si z najčastejších:"
                :selected-keywords="modelValue.interests"
                :suggested-keywords="suggestedKeywords.keywords"
                @select-keyword="addInterest"
            />
        </CFormGroup>

        <CFormGroup
            v-if="show('who')"
            :error="errors.who"
            :label="categoryLabel('who')"
        >
            <CFormCheckboxGroup
                class="columns-1 sm:columns-2"
                :disabled="disabled"
                option-label-key="label"
                option-value-key="id"
                :options="mentoringCategories.who"
                :model-value="modelValue.who || []"
                @update:model-value="update('who', $event)"
            />
        </CFormGroup>
    </div>
</template>

<script setup lang="ts">
import { reactive, computed, watch } from 'vue'
import type { PropType } from 'vue'
import type { ValidationErrors, ValidationResults } from 'last-validator'

import { MentoringCategoriesMapped } from '#shared/types/Mentoring'
import type { MentorProfile } from '#shared/types/User'
import { addArrayItem, scrollToError } from '#shared/utils'
import { MentoringCategoriesMap } from '~~/i18n/mentoring/sk/categories'
import SuggestedKeywordsUtils from '@/utils/suggestedKeywords'
import { validateMentorProfile } from '@/validators/mentorProfile'

const props = defineProps({
    modelValue: {
        type: Object as PropType<MentorProfile>,
        required: true
    },
    disabled: {
        type: Boolean,
        default: false
    },
    onlyMissingFields: {
        type: Boolean,
        default: false
    },
    missingFields: {
        type: Array as PropType<string[]>,
        default: () => []
    }
})

const emit = defineEmits(['update:modelValue'])

const applicationStore = useApplicationStore()
const userProfile = computed(() => applicationStore.userProfile)

// Data
const { who } = MentoringCategoriesMapped
const mentoringCategories = { who }

const errors = reactive<ValidationErrors>({})

// Suggested keywords
const suggestedKeywords = new SuggestedKeywordsUtils('mentorProfileForm', userProfile.value)

// Methods
function show(field: string): boolean {
    return !props.onlyMissingFields || props.missingFields.includes(field)
}

function categoryLabel(categoryId: string): string {
    return Object(MentoringCategoriesMap)[categoryId] || ''
}

function update(key: string, value: any) {
    emit('update:modelValue', {
        ...props.modelValue,
        [key]: value
    })
}

async function validate(): Promise<ValidationResults> {
    const result = await validateMentorProfile(props.modelValue)

    Object.assign(errors, result.errors)

    if (!result.isValid) {
        scrollToError()
    }

    return result
}

function addInterest(interest: string) {
    const interests = addArrayItem(props.modelValue.interests, interest)
    update('interests', interests)
}

// Watchers
watch(() => suggestedKeywords.featuredKeywords, () => {
    suggestedKeywords.resolveFeatured(props.modelValue.interests, addInterest)
})

defineExpose({
    validate
})
</script>